import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Plus } from 'lucide-react';
import { formatDateString } from '@/lib/utils';
import { AddParentModal } from '@/components/AddParentModal';

interface AgeGroup { id: string; group_name: string; sex: string; }
interface WaiverLogRow { id: string; created_at: string; parent_id: string; waiver_name: string; result_code: string; parent?: { id: string; first_name?: string; last_name?: string; email?: string } | null; }

const positionsOptions = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
const competitivenessOptions = ['Premier', 'Elite', 'Alliance'];

const EditPlayer: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'edit'; // Get initial tab from query params

  const isNewPlayer = playerId === 'new';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sex, setSex] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [shirtSize, setShirtSize] = useState('');
  const [ageGroupId, setAgeGroupId] = useState('');
  const [positions, setPositions] = useState<string[]>([]);
  const [competitiveness, setCompetitiveness] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);

  const [waiverLogs, setWaiverLogs] = useState<WaiverLogRow[]>([]);
  const [loadingWaiverLogs, setLoadingWaiverLogs] = useState(false);
  const [waiverError, setWaiverError] = useState<string | null>(null);

  const [registeredParents, setRegisteredParents] = useState<any[]>([]);
  const [invitedParents, setInvitedParents] = useState<any[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [parentsError, setParentsError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddParentModal, setShowAddParentModal] = useState(false);

  useEffect(() => {
    if (!playerId) return;
    const load = async () => {
      setLoading(true);
      try {
        await fetchAgeGroups(); // Fetch age groups regardless
        if (!isNewPlayer) {
          // Only fetch player-specific data if it's an existing player
          await fetchPlayer();
          await fetchWaiverLogs();
          await fetchParents();
        }
      } catch (e: any) {
        console.error(e);
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [playerId, isNewPlayer]); // Add isNewPlayer dependency

  async function fetchPlayer() {
    if (isNewPlayer) return; // Don't fetch if new player
    const { data, error } = await supabase
      .from('players')
      .select('first_name, last_name, sex, birthdate, shirt_size, age_group_id, profile')
      .eq('id', playerId)
      .single();
    if (error) throw error;
    setFirstName(data.first_name || '');
    setLastName(data.last_name || '');
    setSex(data.sex || '');
    setBirthdate(data.birthdate || '');
    setShirtSize(data.shirt_size || '');
    setAgeGroupId(data.age_group_id || '');
    setPositions(data.profile?.positions || []);
    setCompetitiveness(data.profile?.competitiveness || []);
    setNotes(data.profile?.notes || '');
  }

  async function fetchAgeGroups() {
    setLoadingAgeGroups(true);
    try {
      const { data, error } = await supabase
        .from('age_groups')
        .select('id, group_name, sex')
        .order('group_name', { ascending: true });
      if (error) throw error;
      setAgeGroups(data);
    } finally {
      setLoadingAgeGroups(false);
    }
  }

  async function fetchWaiverLogs() {
    if (isNewPlayer) return; // Don't fetch if new player
    setLoadingWaiverLogs(true);
    setWaiverError(null);
    try {
      const { data, error } = await supabase
        .from('waiver_log')
        .select('id, created_at, parent_id, waiver_name, result_code')
        .eq('player_id', playerId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (data?.length) {
        const pids = [...new Set(data.map(l => l.parent_id))];
        const { data: parents, error: pe } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .in('id', pids);
        if (pe) throw pe;
        const map = (parents || []).reduce<Record<string, any>>((acc, p) => { acc[p.id] = p; return acc; }, {});
        setWaiverLogs(data.map(l => ({ ...l, parent: map[l.parent_id] || null })));
      } else setWaiverLogs([]);
    } catch (e: any) {
      console.error(e);
      setWaiverError(e.message || 'Failed waivers');
    } finally {
      setLoadingWaiverLogs(false);
    }
  }

  async function fetchParents() {
    if (isNewPlayer) return; // Don't fetch if new player
    setLoadingParents(true);
    setParentsError(null);
    try {
      const { data: rels, error: re } = await supabase
        .from('player_parent_relationships')
        .select('parent_id')
        .eq('player_id', playerId);
      if (re) throw re;
      const ids = rels.map((r: any) => r.parent_id);
      let regs: any[] = [];
      if (ids.length) {
        const { data: d, error: de } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, phone_number')
          .in('id', ids);
        if (de) throw de;
        regs = d;
      }
      setRegisteredParents(regs);
      const { data: invs, error: ie } = await supabase
        .from('invitation_players')
        .select('invitation_id')
        .eq('player_id', playerId);
      if (ie) throw ie;
      const invIds = invs.map((i: any) => i.invitation_id);
      let inv: any[] = [];
      if (invIds.length) {
        const { data: di, error: de2 } = await supabase
          .from('invitations')
          .select('id, first_name, last_name, email, phone_number, status')
          .eq('status', 'pending')
          .in('id', invIds);
        if (de2) throw de2;
        inv = di;
      }
      setInvitedParents(inv);
    } catch (e: any) {
      console.error(e);
      setParentsError(e.message || 'Failed parents');
    } finally {
      setLoadingParents(false);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const playerData = {
      first_name: firstName,
      last_name: lastName,
      sex: sex,
      birthdate: birthdate || null, // Handle empty string for date
      shirt_size: shirtSize || null,
      age_group_id: ageGroupId || null,
      profile: {
        positions: positions,
        competitiveness: competitiveness,
        notes: notes || null,
      }
    };

    try {
      let result;
      if (isNewPlayer) {
        // Insert new player
        result = await supabase
          .from('players')
          .insert([playerData])
          .select('id') // Select the ID of the newly created player
          .single();
      } else {
        // Update existing player
        result = await supabase
          .from('players')
          .update(playerData)
          .eq('id', playerId);
      }

      const { error } = result;

      if (error) {
        console.error('Error saving player:', error);
        setError(`Failed to save player: ${error.message}`);
      } else {
        if (isNewPlayer && result.data?.id) {
          // Redirect to the newly created player's edit page with the 'parents' tab active
          navigate(`/player/${result.data.id}/edit?tab=parents`, { replace: true });
        } else if (!isNewPlayer) {
          // Optionally show a success message or just refetch data
          console.log('Player updated successfully');
          // Maybe refetch data if needed, or navigate away
          // navigate('/player-management'); // Example navigation
        } else {
          // Handle case where insert didn't return ID (shouldn't happen with .single())
           navigate('/player-management'); // Fallback navigation
        }
      }
    } catch (e: any) {
      console.error('Exception saving player:', e);
      setError(`An unexpected error occurred: ${e.message}`);
    }
  }

  // Helper to group age groups by sex
  const groupedAgeGroups = ageGroups.reduce((acc, group) => {
    const key = group.sex.charAt(0).toUpperCase() + group.sex.slice(1); // Capitalize first letter
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(group);
    return acc;
  }, {} as Record<string, AgeGroup[]>);

  if (loading) return <div className="py-10 text-center">Loading...</div>;
  if (error) return <div className="py-10 text-center text-red-500">{error}</div>;

  return (
    <>
      <div className="p-6 space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isNewPlayer ? 'Create New Player' : `Edit Player: ${firstName} ${lastName}`}</h1>
        <Tabs defaultValue={initialTab} className="space-y-4">
          {!isNewPlayer && (
            <TabsList>
              <TabsTrigger value="edit">Edit Info</TabsTrigger>
              <TabsTrigger value="waivers">Waiver Logs</TabsTrigger>
              <TabsTrigger value="parents">Parents</TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="edit">
            <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg shadow">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <Select value={sex} onValueChange={v => setSex(v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Select Gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birthdate</label>
                <Input type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shirt Size</label>
                <Select value={shirtSize} onValueChange={v => setShirtSize(v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Size" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="Youth XS">Youth XS</SelectItem>
                    <SelectItem value="Youth S">Youth S</SelectItem>
                    <SelectItem value="Youth M">Youth M</SelectItem>
                    <SelectItem value="Youth L">Youth L</SelectItem>
                    <SelectItem value="Youth XL">Youth XL</SelectItem>
                    <SelectItem value="Adult S">Adult S</SelectItem>
                    <SelectItem value="Adult M">Adult M</SelectItem>
                    <SelectItem value="Adult L">Adult L</SelectItem>
                    <SelectItem value="Adult XL">Adult XL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                <Select value={ageGroupId} onValueChange={v => setAgeGroupId(v)} disabled={loadingAgeGroups}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Select Age Group" /></SelectTrigger>
                  <SelectContent>
                    {loadingAgeGroups ? (
                      <SelectItem value="" disabled>Loading...</SelectItem>
                    ) : Object.entries(groupedAgeGroups).length > 0 ? (
                      Object.entries(groupedAgeGroups).map(([sex, groups]) => (
                        <SelectGroup key={sex}>
                          <SelectLabel>{sex}</SelectLabel>
                          {groups.map(g => (
                            <SelectItem key={g.id} value={g.id}>{g.group_name}</SelectItem>
                          ))}
                        </SelectGroup>
                      ))
                    ) : (
                      <SelectItem value="" disabled>No age groups available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <p className="font-medium text-gray-700">Positions</p>
                <div className="flex flex-wrap gap-2">
                  {positionsOptions.map(opt => (
                    <div key={opt} className="flex items-center space-x-2">
                      <Checkbox
                        id={`pos-${opt}`}
                        checked={positions.includes(opt)}
                        onCheckedChange={c => setPositions(c ? [...positions, opt] : positions.filter(p => p !== opt))}
                      />
                      <label htmlFor={`pos-${opt}`} className="text-sm text-gray-700">{opt}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="font-medium text-gray-700">Competitiveness</p>
                <div className="flex flex-wrap gap-2">
                  {competitivenessOptions.map(opt => (
                    <div key={opt} className="flex items-center space-x-2">
                      <Checkbox
                        id={`comp-${opt}`}
                        checked={competitiveness.includes(opt)}
                        onCheckedChange={c => setCompetitiveness(c ? [...competitiveness, opt] : competitiveness.filter(p => p !== opt))}
                      />
                      <label htmlFor={`comp-${opt}`} className="text-sm text-gray-700">{opt}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          </TabsContent>
          {!isNewPlayer && (
            <>
              <TabsContent value="waivers">
                <div className="bg-white p-4 rounded-lg shadow">
                  {loadingWaiverLogs ? <p>Loading waiver logs...</p> : waiverError ? <p className="text-red-500">{waiverError}</p> : waiverLogs.length ? (
                    <Table>
                      <TableHeader>
                        <TableHead>Date</TableHead>
                        <TableHead>Waiver</TableHead>
                        <TableHead>Result</TableHead>
                        <TableHead>Signed By</TableHead>
                      </TableHeader>
                      <TableBody>
                        {waiverLogs.map(log => (
                          <TableRow key={log.id}>
                            <TableCell>{formatDateString(log.created_at)}</TableCell>
                            <TableCell>{log.waiver_name}</TableCell>
                            <TableCell><Badge>{log.result_code}</Badge></TableCell>
                            <TableCell>{log.parent?.first_name} {log.parent?.last_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : <p className="italic text-gray-500">No waiver logs found.</p>}
                </div>
              </TabsContent>
              <TabsContent value="parents">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => setShowAddParentModal(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Add Parent
                  </Button>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                  {loadingParents ? <p>Loading parents...</p> : parentsError ? <p className="text-red-500">{parentsError}</p> : (
                    (registeredParents.length || invitedParents.length) ? (
                      <div className="flex flex-col gap-4">
                        {registeredParents.length > 0 && (
                          <div>
                            <h3 className="font-semibold mb-2">Registered Parents</h3>
                            <ul className="divide-y divide-gray-200">
                              {registeredParents.map(p => (
                                <li key={p.id} className="py-2">
                                  <p>{p.first_name} {p.last_name}</p>
{p.phone_number && (
  <p className="text-sm text-gray-600">{p.phone_number}</p>
)}
<p className="text-sm text-gray-600">{p.email}</p>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {invitedParents.length > 0 && (
                          <div>
                            <h3 className="font-semibold mb-2">Invited Parents</h3>
                            <ul className="divide-y divide-gray-200">
                              {invitedParents.map(inv => (
                                <li key={inv.id} className="py-2 opacity-80">
                                  <p>{inv.first_name} {inv.last_name}<Badge variant="outline" className="ml-2">Pending</Badge></p>
{inv.phone_number && (
  <p className="text-sm text-gray-600">{inv.phone_number}</p>
)}
<p className="text-sm text-gray-600">{inv.email}</p>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : <p className="italic text-gray-500">No parents or invites.</p>
                  )}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
      {showAddParentModal && !isNewPlayer && (
        <AddParentModal
          playerId={playerId!}
          onClose={() => setShowAddParentModal(false)}
          onParentAdded={() => fetchParents()}
        />
      )}
    </>
  );
};

export default EditPlayer;
